import { useCallback, useEffect, useState } from 'react';
import { generatePath, useLocation } from 'react-router';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { DocumentApi } from 'Api/Document/DocumentApi';
import { GovernanceApi } from 'Api/Governance/GovernanceApi';
import { AdvancedFeatureUnavailableExplanation } from 'Components/AdvancedFeatureUnavailableExplanation/AdvancedFeatureUnavailableExplanation';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { ViewGovernanceLibraryDefinitionModal, ViewGovernanceLibraryDefinitionModalProps } from 'Components/Governance/ViewGovernanceLibraryDefinitionModal/ViewGovernanceLibraryDefinitionModal';
import { AssociatedControlsModal, AssociatedControlsModalProps } from 'Components/Modal/AssociatedControlsModal/AssociatedControlsModal';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { PrimaryTabs, Tab } from 'Components/Tabs/PrimaryTabs/PrimaryTabs';
import { GOVERNANCE_CREATE } from 'Config/PathTemplates';
import { Navigator } from 'Helpers/Navigator';
import { AiGeneratedDoc } from 'Models/ArtificialIntelligence';
import { AdvancedFeature } from 'Models/ClientDetailsModel';
import { GovernanceVersion, TextBasedGovernanceVersion } from 'Models/Governance';
import { OperationalControl } from 'Models/OperationalControls';

import { ActiveGovernanceTab, ActiveGovernanceTabProps } from './ActiveGovernanceTab/ActiveGovernanceTab';
import { AiDocGeneratorTab } from './AiDocGeneratorTab/AiDocGeneratorTab';
import { RetiredGovernanceTab, RetiredGovernanceTabProps } from './RetiredGovernanceTab/RetiredGovernanceTab';

export interface GovernanceLibraryProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    documentApi: DocumentApi;
    governanceApi: GovernanceApi;
    navigator: Navigator;
}

enum PageElements {
    GovernanceCommentModal,
    AssociatedControlModal,
    None,
}

const ACTIVE_TAB_KEY = 'activeGovernance';
export const RETIRED_TAB_KEY = 'retiredGovernance';
export const AI_TAB_KEY = 'aiDocGenerator';

export const GovernanceLibrary = (props: GovernanceLibraryProps) => {
    const cachedData = useCachedData();
    const aiDocGeneratorAvailability = cachedData.checkAdvancedFeatureAvailability(AdvancedFeature.AI_DOCUMENT_GENERATOR);

    const location = useLocation();
    const [activeVersions, setActiveVersions] = useState<GovernanceVersion[]>();
    const [retiredVersions, setRetiredVersions] = useState<GovernanceVersion[]>();
    const [allAiGeneratedDocs, setAllAiGeneratedDocs] = useState<AiGeneratedDoc[]>();
    const [aiGeneratedDocsLoadingErrorOccurred, setAiGeneratedDocsLoadingErrorOccurred] = useState(false);
    const [displayedElement, setDisplayedElement] = useState<PageElements>(PageElements.None);
    const [textBasedGovernanceBeingViewed, setTextBasedGovernanceBeingViewed] = useState<TextBasedGovernanceVersion>();
    const [associatedControls, setAssociatedControls] = useState<OperationalControl[]>();
    const [governanceWithAssociatedControls, setGovernanceWithAssociatedControls] = useState<GovernanceVersion>();

    const handleRequestError = (error: Error): void => console.error('Error: ', error);

    const defaultActiveTab = (() => {
        if (location.hash && location.hash.length > 0) {
            return location.hash.substring(1);
        }

        return ACTIVE_TAB_KEY;
    })();

    const getActiveVersions = useCallback(async (): Promise<void> => {
        try {
            const versions = (await props.governanceApi.getAllActiveVersions()).data;
            setActiveVersions(versions);
        } catch (error) {
            handleRequestError(error);
        }
    }, [props.governanceApi]);

    const getRetiredVersions = useCallback(async (): Promise<void> => {
        try {
            const versions = (await props.governanceApi.getAllRetiredVersions()).data;
            setRetiredVersions(versions);
        } catch (error) {
            handleRequestError(error);
        }
    }, [props.governanceApi]);

    const aiDocGeneratorGetAll = useCallback(async (): Promise<void> => {
        try {
            const response = await props.artificialIntelligenceApi.aiDocGeneratorGetAll();
            setAllAiGeneratedDocs(response.data);
        } catch (error) {
            console.error(error);
            setAiGeneratedDocsLoadingErrorOccurred(true);
        }
    }, [props.artificialIntelligenceApi]);

    const aiDocGeneratorDelete = async (aiGeneratedDoc: AiGeneratedDoc): Promise<string> => {
        await props.artificialIntelligenceApi.aiDocGeneratorDelete(aiGeneratedDoc);
        await aiDocGeneratorGetAll();
        return `Deleted ${aiGeneratedDoc.title}`;
    };

    const updateGovernanceListing = useCallback((): void => {
        getActiveVersions();
        getRetiredVersions();
        aiDocGeneratorGetAll();
    }, [getActiveVersions, getRetiredVersions, aiDocGeneratorGetAll]);

    useEffect(() => {
        updateGovernanceListing();
    }, [updateGovernanceListing]);

    const displayGovernanceTextModal = (governance: TextBasedGovernanceVersion): void => {
        setTextBasedGovernanceBeingViewed(governance);
        setDisplayedElement(PageElements.GovernanceCommentModal);
    };

    const displayGovernanceWithAssociatedControlsModal = (controls: OperationalControl[]): void => {
        const governanceVersions = [...(activeVersions ?? []), ...(retiredVersions ?? [])];
        setGovernanceWithAssociatedControls(governanceVersions.find((governance) => governance.associated_controls === controls));
        setAssociatedControls(controls);
        setDisplayedElement(PageElements.AssociatedControlModal);
    };

    const viewGovernanceLibraryDefinitionModalProps = (governance: TextBasedGovernanceVersion): ViewGovernanceLibraryDefinitionModalProps => {
        return {
            hideModal: () => setDisplayedElement(PageElements.None),
            governance: governance,
        };
    };

    const associatedControlModalProps = (controls: OperationalControl[]): AssociatedControlsModalProps => {
        if (!governanceWithAssociatedControls) {
            return {
                hideModal: () => {
                    setDisplayedElement(PageElements.None);
                    setGovernanceWithAssociatedControls(undefined);
                },
                associatedControls: controls,
            };
        }

        return {
            hideModal: () => {
                setDisplayedElement(PageElements.None);
                setGovernanceWithAssociatedControls(undefined);
            },
            associatedControls: controls,
            mapControlsTo: generatePath(GOVERNANCE_CREATE, { governance_id: governanceWithAssociatedControls.id }),
        };
    };

    if (activeVersions && retiredVersions) {
        const activeGovernanceTabProps: ActiveGovernanceTabProps = {
            documentApi: props.documentApi,
            governanceItems: activeVersions,
            openExternalUrl: (url) => props.navigator.openExternalUrl(url, true),
            displayGovernanceTextModal: displayGovernanceTextModal,
            displayAssociatedControlsModal: displayGovernanceWithAssociatedControlsModal,
        };

        const retiredGovernanceTabProps: RetiredGovernanceTabProps = {
            documentApi: props.documentApi,
            governanceItems: retiredVersions,
            openExternalUrl: (url) => props.navigator.openExternalUrl(url, true),
            displayGovernanceTextModal: displayGovernanceTextModal,
            displayAssociatedControlsModal: displayGovernanceWithAssociatedControlsModal,
        };

        return (
            <>
                {displayedElement === PageElements.GovernanceCommentModal && textBasedGovernanceBeingViewed && <ViewGovernanceLibraryDefinitionModal {...viewGovernanceLibraryDefinitionModalProps(textBasedGovernanceBeingViewed)} />}
                {displayedElement === PageElements.AssociatedControlModal && associatedControls && <AssociatedControlsModal {...associatedControlModalProps(associatedControls)} />}
                <PageLayoutDefault
                    headerTitle="Governance Integration"
                    body={[
                        {
                            content: (
                                <PrimaryTabs defaultActiveTab={defaultActiveTab} removePadding>
                                    <Tab eventKey={ACTIVE_TAB_KEY} title="Active">
                                        <ActiveGovernanceTab {...activeGovernanceTabProps} />
                                    </Tab>
                                    <Tab eventKey={RETIRED_TAB_KEY} title="Retired">
                                        <RetiredGovernanceTab {...retiredGovernanceTabProps} />
                                    </Tab>
                                    <Tab eventKey={AI_TAB_KEY} title="SummIT Guide™">
                                        {aiDocGeneratorAvailability.isAvailable ? <AiDocGeneratorTab allAiGeneratedDocs={allAiGeneratedDocs} aiDocGeneratorDelete={aiDocGeneratorDelete} loadingErrorOccurred={aiGeneratedDocsLoadingErrorOccurred} documentApi={props.documentApi} /> : <AdvancedFeatureUnavailableExplanation feature={aiDocGeneratorAvailability.feature} reason={aiDocGeneratorAvailability.reason} />}
                                    </Tab>
                                </PrimaryTabs>
                            ),
                        },
                    ]}
                />
            </>
        );
    } else {
        return <Placeholder />;
    }
};
